import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
    SquaresFour,
    Truck,
    ClipboardText,
    Package,
    Tire,
    BatteryCharging,
    Drop,
    CalendarCheck,
    Storefront,
    MapPin,
    ChartBar,
    UsersThree,
    SignOut,
    IdentificationCard,
    ShieldCheck,
    Gavel,
    CaretDown,
    Gauge,
    GasPump,
} from "@phosphor-icons/react";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
    { to: "/", label: "Dashboard", icon: SquaresFour, end: true, roles: ["admin", "fleet_manager", "store_manager", "technician", "viewer"] },
    { to: "/vehicles", label: "Vehicles", icon: Truck, roles: ["admin", "fleet_manager", "technician", "viewer"] },
    { to: "/update-km", label: "Update Km", icon: Gauge, roles: ["admin", "fleet_manager", "store_manager", "technician", "viewer"] },
    { to: "/mileage", label: "Mileage", icon: GasPump, roles: ["admin", "fleet_manager", "store_manager", "technician", "viewer"] },
    { to: "/drivers", label: "Drivers", icon: IdentificationCard, roles: ["admin", "fleet_manager", "viewer"] },
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, roles: ["admin", "fleet_manager", "viewer"] },
    { to: "/work-orders", label: "Work Orders", icon: ClipboardText, roles: ["admin", "fleet_manager", "technician", "viewer"] },
    { to: "/inventory", label: "Inventory", icon: Package, roles: ["admin", "store_manager", "fleet_manager", "viewer"] },
    { to: "/tyres", label: "Tyres", icon: Tire, roles: ["admin", "fleet_manager", "store_manager", "viewer"] },
    { to: "/batteries", label: "Batteries", icon: BatteryCharging, roles: ["admin", "fleet_manager", "store_manager", "viewer"] },
    { to: "/consumables", label: "Consumables", icon: Drop, roles: ["admin", "fleet_manager", "store_manager", "viewer"] },
    { to: "/pm-schedules", label: "PM Schedules", icon: CalendarCheck, roles: ["admin", "fleet_manager", "viewer"] },
    { to: "/vendors", label: "Vendors", icon: Storefront, roles: ["admin", "fleet_manager", "store_manager", "viewer"] },
    { to: "/locations", label: "Locations", icon: MapPin, roles: ["admin", "fleet_manager", "viewer"] },
    { to: "/approvals", label: "Approvals", icon: Gavel, roles: ["admin"] },
    { to: "/reports", label: "Reports", icon: ChartBar, roles: ["admin", "fleet_manager", "store_manager", "viewer"] },
    { to: "/users", label: "Users", icon: UsersThree, roles: ["admin"] },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const allowed = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

    // Toggle the body class so the global viewer-lock CSS can hide all
    // mutating controls (Add / Edit / Del / Switch / Remove / Scrap, etc.).
    useEffect(() => {
        const cls = "role-viewer";
        if (user?.role === "viewer") document.body.classList.add(cls);
        else document.body.classList.remove(cls);
        return () => document.body.classList.remove(cls);
    }, [user?.role]);

    return (
        <div className="min-h-screen flex bg-fleet-bg text-fleet-ink">
            {/* Sidebar */}
            <aside
                className={`${collapsed ? "w-16" : "w-60"} shrink-0 border-r border-fleet-border bg-white flex flex-col transition-all`}
                data-testid="sidebar"
            >
                <div className="h-14 px-4 flex items-center gap-2 border-b border-fleet-border">
                    <div className="h-7 w-7 rounded-sm bg-fleet-ink flex items-center justify-center">
                        <span className="font-display font-bold text-white text-sm">E</span>
                    </div>
                    {!collapsed && (
                        <div className="font-display font-semibold tracking-tight text-fleet-ink">
                            EAZYFLEET
                        </div>
                    )}
                </div>

                <nav className="flex-1 py-3 overflow-y-auto">
                    {allowed.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-slate-100 text-fleet-ink font-medium"
                                            : "text-fleet-inkSoft hover:bg-slate-50 hover:text-fleet-ink"
                                    }`
                                }
                                data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
                            >
                                <Icon size={18} weight="regular" />
                                {!collapsed && <span>{item.label}</span>}
                            </NavLink>
                        );
                    })}
                </nav>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 px-6 border-b border-fleet-border bg-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setCollapsed((c) => !c)}
                            className="text-fleet-inkSoft hover:text-fleet-ink"
                            data-testid="sidebar-toggle"
                            aria-label="Toggle sidebar"
                        >
                            <SquaresFour size={20} />
                        </button>
                    </div>
                    <div className="flex items-center gap-3 relative">
                        <NotificationsBell />
                        <button
                            type="button"
                            onClick={() => setProfileOpen((o) => !o)}
                            onBlur={() => setTimeout(() => setProfileOpen(false), 150)}
                            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-slate-50 transition-colors"
                            data-testid="profile-menu-trigger"
                        >
                            <div className="h-8 w-8 rounded-full bg-fleet-ink text-white flex items-center justify-center text-sm font-medium">
                                {user?.name?.[0]?.toUpperCase() || "U"}
                            </div>
                            <div className="hidden md:flex flex-col items-start leading-tight">
                                <span className="text-sm font-medium truncate max-w-[140px]">{user?.name}</span>
                                <span className="text-[10px] uppercase tracking-wider text-fleet-inkSoft">{user?.role?.replace("_", " ")}</span>
                            </div>
                            <CaretDown size={12} className="text-fleet-inkSoft" />
                        </button>

                        {profileOpen && (
                            <div
                                className="absolute right-0 top-full mt-2 w-56 bg-white border border-fleet-border rounded-md shadow-lg z-40 overflow-hidden"
                                data-testid="profile-menu"
                            >
                                <div className="px-4 py-3 border-b border-fleet-border">
                                    <div className="text-sm font-medium truncate">{user?.name}</div>
                                    <div className="text-xs text-fleet-inkSoft truncate">{user?.email}</div>
                                </div>
                                <button
                                    onMouseDown={async () => {
                                        await logout();
                                        navigate("/login");
                                    }}
                                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-fleet-inkSoft hover:bg-slate-50 hover:text-fleet-critical transition-colors"
                                    data-testid="logout-btn"
                                >
                                    <SignOut size={16} />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto" data-testid="main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
